import { Link, createFileRoute } from "@tanstack/react-router";
import { Suspense, useCallback } from "react";
import { z } from "zod/v4";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoadingSpinner } from "@/components/loading";
import { Card, CardContent, CardTitle } from "@/components/ui/card";

const allProductsPageSearchParamsSchema = z.object({
  tab: z
    .enum(["privacy", "provenance", "generation", "ai-humanizer"])
    .default("ai-humanizer"),
});
type allProductsPageSearchParamsSchema = z.infer<
  typeof allProductsPageSearchParamsSchema
>;

export const Route = createFileRoute("/_app/all-products")({
  validateSearch: allProductsPageSearchParamsSchema,
  component: RouteComponent,
  staticData: {
    title: "All Products",
  },
  head: () => ({
    meta: [
      {
        name: "description",
        content:
          "Explore all available AI tools within your plan. Access all available services in one place.",
      },
    ],
  }),
});

function RouteComponent() {
  const { tab = "privacy" } = Route.useSearch();

  const navigate = Route.useNavigate();

  const setTab = useCallback(
    (tab: string) =>
      navigate({
        search: { tab: tab as allProductsPageSearchParamsSchema["tab"] },
        replace: true,
        resetScroll: false,
      }),
    [navigate],
  );

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">All Products</h1>
        <p className="mt-1 text-gray-500">
          Browse all our products and services in one place.
        </p>
      </div>

      <Tabs
        value={tab}
        onValueChange={setTab}
        className="w-full"
        orientation="vertical"
      >
        <TabsList className="mb-8 flex w-full gap-2 rounded-lg p-1 sm:grid sm:grid-cols-3 sm:gap-0 dark:bg-black">
          <TabsTrigger
            className="rounded-md max-md:text-[12px]"
            value="privacy"
          >
            Data Privacy
          </TabsTrigger>
          <TabsTrigger
            className="rounded-md max-md:text-[12px]"
            value="ai-humanizer"
          >
            AI Humanizer
          </TabsTrigger>
          <TabsTrigger
            className="rounded-md max-md:text-[12px]"
            value="provenance"
          >
            Data Provenance
          </TabsTrigger>
          {/* <TabsTrigger
            className="rounded-md max-md:text-[12px]"
            value="generation"
          >
            Content Generation
          </TabsTrigger> */}
        </TabsList>
        <TabsContent value="generation">
          <Suspense fallback={<LoadingSpinner />}></Suspense>
        </TabsContent>

        <TabsContent value="privacy" className="mt-0">
          <CardSection
            sectionTitle="Data Privacy"
            sectionDescription="Protect confidential data while enabling AI adoption."
            cards={dataPrivacyCards}
          />
        </TabsContent>

        <TabsContent value="provenance">
          <CardSection
            sectionTitle="Data Provenance"
            sectionDescription="Track the origin of data with full transparency."
            cards={dataProvenanceCards}
          />
        </TabsContent>

        {/* <TabsContent value="generation">
          <CardSection
            sectionTitle="Content Generation"
            sectionDescription="Create high-quality, AI-powered content with built-in humanization."
            cards={contentGenerationCards}
          />
        </TabsContent> */}

        <TabsContent value="ai-humanizer">
          <CardSection
            sectionTitle="AI Humanizer"
            sectionDescription="Transform AI-generated text into human-like writing that feels authentic, bypass spam filters. "
            cards={aiHumanizerCards}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const dataPrivacyCards = [
  {
    title: "Secure LLM Chat",
    description: "Chat safely with AI, masking sensitive data.",
    icon: "/icons/message-chat-circle.svg",
    link: "/secure-llm",
  },

  {
    title: "Document Redactor",
    description: "Analyze documents for fraud patterns.",
    icon: "/icons/pen-edit-icon.svg",
    link: "/document-redactor",
  },
  {
    title: "Deepfake Audio Detection",
    description: "Determine if auido content is human or AI-generated voice.",
    icon: "/icons/audio-lines.svg",
    link: "/vexon",
  },
  // {
  //   title: "File System Scan",
  //   description: "Scan folders for PII risks and show risk levels.",
  //   icon: "/icons/shield-icon.svg",
  //   link: "",
  //   disabled: true,
  // },
  // {
  //   title: "Data Based Anonymizer",
  //   description: "Anonymize databases to remove PII.",
  //   icon: "/icons/drops-icon.svg",
  //   link: "",
  //   disabled: true,
  // },
];

export const dataProvenanceCards = [
  {
    title: "AI Detector",
    description:
      "Determine if content is human-written, AI-generated, or altered.",
    icon: "/icons/stars-icon.svg",
    link: "/ai-detector",
  },
  // {
  //   title: "AI Highlighter",
  //   description: "Highlight AI-generated, human-generated, or altered content.",
  //   icon: "/icons/pen-icon.svg",
  //   link: "/ai-highlighter",
  // },
  {
    title: "Humanized Text Detector",
    description: "Check if text is humanized or retains AI traces.",
    icon: "/icons/person-icon.svg",
    link: "/humanized-text-detector",
  },
  {
    title: "Copyright Detector",
    description: "Identify copyrighted content and sources.",
    icon: "/icons/copyright-icon.svg",
    link: "/copyright-detector",
  },
  {
    title: "Writing Analysis",
    description: "Analyze text quality and statistics.",
    icon: "/icons/analysis-icon.svg",
    link: "/writing-analysis",
  },
];

export const contentGenerationCards = [
  {
    title: "Stealth AI",
    description: "Create or enhance content with real-time AI.",
    icon: "/icons/shuriken-icon.svg",
    link: "/stealth-ai-writer",
  },
  {
    title: "Content Enhancer",
    description: "Enhance your content with AI.",
    icon: "/icons/wand-icon.svg",
    link: "/content-enhancer",
  },
];

export const aiHumanizerCards = [
  {
    title: "AI Humanizer (Beta)",
    description: "Transform AI-generated text into human-like writing.",
    icon: "/icons/users-01.svg",
    link: "/content-humanizer",
  },
];
type CardData = {
  title: string;
  description: string;
  icon: string;
  link: string;
  disabled?: boolean;
};

type CardSectionProps = {
  sectionTitle: string;
  sectionDescription: string;
  cards: Array<CardData>;
};

export function CardSection({
  sectionTitle,
  sectionDescription,
  cards,
}: CardSectionProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-black">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">{sectionTitle}</h2>
        <p className="mt-1 text-gray-500">{sectionDescription}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((card, idx) => (
          <Link
            key={idx}
            to={card.link || "#"}
            onClick={(e) => {
              if (card.disabled) {
                e.preventDefault();
                toast.info("Coming soon");
              }
            }}
          >
            <Card
              className={`h-64 border border-gray-200 dark:border-gray-800 ${
                card.disabled ? "cursor-not-allowed opacity-50" : ""
              }`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col items-start">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                    <img
                      src={card.icon}
                      alt={`${card.title} Icon`}
                      className="h-6 w-6 text-gray-500"
                    />
                  </div>
                  <CardTitle className="mb-2 text-lg font-semibold">
                    {card.title}
                  </CardTitle>
                  <p className="text-sm text-gray-500">{card.description}</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
